"use client";

import React from "react";
import { cn } from "utils/utils";
import { FinishEnum } from "modules/cards/model/card";
import { CardPreviewLoading } from "modules/common/components/CardPreviewLoading";
import { useCardDetailsModal } from "modules/cards/hooks/useCardDetailsModal";
import { useCardGridItemContext } from "./CardGridItemContext";
import {
  hasFoilVisual,
  primaryDisplayFinish,
} from "modules/cards/model/finishBehavior";
import type { ImageProps } from "next/image";

interface CardGridItemImageProps {
  /** Override default click handler (open card details modal) */
  onClick?: () => void;
  /** Describes a custom click action when it is not card selection. */
  ariaLabel?: string;
  priority?: boolean;
  loading?: ImageProps["loading"];
  disabled?: boolean;
  disabledLabel?: string;
}

export function CardGridItemImage({
  onClick,
  ariaLabel: customAriaLabel,
  priority,
  loading,
  disabled = false,
  disabledLabel,
}: CardGridItemImageProps) {
  const { item, sizing, selection } = useCardGridItemContext();
  const { handleOpenCardDetailsByDataId } = useCardDetailsModal();

  const isFoil = hasFoilVisual(item.finishes);
  const displayFinish = primaryDisplayFinish(item.finishes);

  const openDetails = () =>
    handleOpenCardDetailsByDataId(item.dataId, item.game, displayFinish);

  // Once any card in this grid is selected, every card becomes a toggle
  // target — clicking the art selects/deselects it instead of running
  // whatever the cell's click normally does (open details, open an edit
  // modal, etc.). Centralized here so callers don't each reimplement it.
  const isToggle = selection.modeType !== "none" && selection.isAnySelected;

  const handleClick = isToggle ? selection.onToggle : (onClick ?? openDetails);

  let ariaLabel: string;
  if (disabled) {
    ariaLabel = disabledLabel ?? `${item.name} is unavailable`;
  } else if (isToggle) {
    if (selection.clearsAll) {
      ariaLabel = "Clear selection";
    } else {
      ariaLabel =
        selection.state !== "none"
          ? `Deselect ${item.name}`
          : `Select ${item.name}`;
    }
  } else if (customAriaLabel) {
    ariaLabel = customAriaLabel;
  } else if (onClick) {
    // A custom onClick does something other than open details (the proposal
    // picker adds the card to the trade), so don't announce "View details".
    // Callers with a more specific verb pass `ariaLabel`.
    ariaLabel = item.name;
  } else {
    ariaLabel = `View details for ${item.name}`;
  }

  return (
    <button
      type="button"
      className={cn(
        "block w-full",
        // A disabled button doesn't inherit the cell's cursor, so it needs its
        // own — matching the inert checkbox the viewer just tried instead.
        disabled && "cursor-not-allowed",
        selection.state !== "none" &&
          "rounded-tl-[4.75%_3.5%] rounded-tr-[4.75%_3.5%]"
      )}
      onClick={disabled ? undefined : handleClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      <CardPreviewLoading
        sizing={sizing}
        card={item}
        foil={isFoil}
        priority={priority}
        loading={loading}
      />
    </button>
  );
}
